import { useEffect, useState, useRef, useMemo } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Chip,
  FormControlLabel,
  Switch,
  Stack,
} from "@mui/material";

import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import "moment/locale/en-gb";
import DynamicBudgetInput from "./EditProject/dynamicBudgetInput";
import DynamicRevenueSharing from "./EditProject/dynamicRevenueSharing";
import ProjectMembersManager from "./EditProject/projectMembersManager.js";
import Attachments from "./EditProject/attachments.js";
import { useError } from "../Context/errorContext.js";
import { fmtDate } from "./helpers.js";
import { allowedExtensions, allowedTypes, statuses } from "./helpers.js";
import MyHoursCompare from "./myHoursCompare.js";
const moment = require("moment");

const statusOptions = Object.entries(statuses).map(
  ([status_name, status_id]) => ({
    status_name,
    status_id,
  }),
);

const EditProjectForm = ({
  form,
  handleInputChange,
  handleSubmit,
  handleSave,
  resultMessage,
  units,
  categories,
  employees,
  members,
  setMembers,
  expensesConfirmers,
  isDraft,
  setIsDraft,
  user,
  budget,
  setBudget,
  revenueSharing,
  setRevenueSharing,
  managerProjects,
  autofillProjectForm,
  uploads,
  setUploads,
  saving,
  timeEditTermCategories,
}) => {
  const [pendingFormData, setPendingFormData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ebsTotal, setEbsTotal] = useState(0);
  const [unitId, setUnitId] = useState(form.unit_id);
  const [budgetError, setBudgetError] = useState("");
  const [save, setSave] = useState(false);
  const [internalStaffCost, setInternalStaffCost] = useState(0);
  const [autofillProject, setAutofillProject] = useState(null);
  const { setAlert } = useError();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [mhConfirmDialogOpen, setMhConfirmDialogOpen] = useState(false);

  const errorRef = useRef(null);
  const descriptionRef = useRef(null);

  const isAccountant = user.userData.userRoles.includes("Accounting");
  const isManager = user.userData.userRoles.includes("Unit Manager");
  const isAdmin = user.userData.userRoles.includes("Administrator");

  const maxLength = 2500;

  const tooLong = useMemo(() => {
    return form.description?.length > maxLength;
  }, [form.description]);

  function validateFiles(fileList) {
    const MAX_MB = 10;
    for (const obj of fileList) {
      if (obj.uid) continue;
      const f = obj.file;
      console.log(f.type, f.size);
      const typeOk = allowedTypes.has(f.type); // MIME type
      const sizeOk = f.size <= MAX_MB * 1024 * 1024; // size
      console.log(typeOk, sizeOk);
      if (!typeOk || !sizeOk) return false;
    }
    return true;
  }

  const allExpensesConfirmers = useMemo(() => {
    if (
      !expensesConfirmers.find((e) => e.email === form.project_manager_email) ||
      null
    ) {
      return [
        {
          name: form.project_manager_name,
          email: form.project_manager_email,
        },
        ...expensesConfirmers,
      ];
    } else {
      return [...expensesConfirmers];
    }
  }, [
    form.project_manager_email,
    form.project_manager_name,
    expensesConfirmers,
  ]);

  const activityOptions = useMemo(() => {
    if (form?.unit_id) {
      const unit = units?.find((u) => u.unit_id === form.unit_id);
      return unit?.activities || [];
    }
    return null;
  }, [form, units]);

  const activity = useMemo(() => {
    return (
      (activityOptions ?? []).find(
        (activity) => activity.activity_id === form.activity_id,
      ) || null
    );
  }, [form.activity_id]);

  useEffect(() => {
    setUnitId(form.unit_id);
  }, [form.unit_id]);

  useEffect(() => {
    if (
      !(activityOptions ?? []).find(
        (activity) => activity.activity_id === form.activity_id,
      )
    ) {
      handleInputChange({
        target: {
          id: "activity_id",
          value: null,
        },
      });
    }
  }, [activityOptions]);

  useEffect(() => {
    setBudgetError("");
  }, [revenueSharing]);

  useEffect(() => {
    if (!autofillProject?.uid) return;
    autofillProjectForm(autofillProject?.uid);
  }, [autofillProject]);

  useEffect(() => {
    if (!saving) {
      setMhConfirmDialogOpen(false);
    }
  }, [saving]);

  return (
    <Box>
      <Stack direction="row" spacing={6} sx={{ alignItems: "center" }}>
        <Typography variant="h4" gutterBottom>
          Project Application
        </Typography>
        {(isAdmin || form.unit_id === 3) && form.project_id === null && (
          <Autocomplete
            options={managerProjects}
            getOptionLabel={(option) => `${option.name} (${option.code})`}
            isOptionEqualToValue={(option, value) =>
              option.project_id === value.project_id
            }
            value={autofillProject}
            onChange={(event, newValue) => {
              setAutofillProject(newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select project to autofill form"
                variant="outlined"
                margin="normal"
                fullWidth
                sx={{ width: 400 }}
              />
            )}
          />
        )}
      </Stack>
      <form
        className="formContainer"
        onKeyDown={(e) => {
          // block Enter everywhere except multiline textareas
          if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
            e.preventDefault();
          }
        }}
        onSubmit={(e) => {
          e.preventDefault(); // Prevent auto submit

          if (!validateFiles(uploads)) {
            setAlert({ message: "invalid files", severity: "error" });
            return;
          }
          if (isDraft) {
            if (form.status_id > statuses.DRAFT) {
              const confirm = window.confirm(
                `Saving this project as Draft will reset the project status to Draft!`,
              );
              if (!confirm) return;
            }
            if (form.syncToMyHours && !form.name) {
              setAlert({
                message: "Project name is required for My Hours sync!",
                severity: "error",
              });
              return;
            }
            if (form.syncToMyHours) {
              setMhConfirmDialogOpen(true);
            } else {
              handleSave();
            }
            return;
          }
          /* if (ebsTotal < 0) {
            setBudgetError(`EBS revenue must not be negative!`);
            // Scroll to error
            setTimeout(() => {
              errorRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }, 0);
            return;
          } */
          if (tooLong) {
            setTimeout(() => {
              descriptionRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }, 0);
            return;
          }

          setBudgetError("");
          if (e.target.checkValidity()) {
            if (save) {
              if (form.syncToMyHours) {
                setMhConfirmDialogOpen(true);
              } else {
                handleSave();
              }
              return;
            }
            setPendingFormData({ isDraft: false }); // Store pending submit
            setConfirmDialogOpen(true);
          } else {
            e.target.reportValidity(); // Show browser's validation errors
          }
        }}
      >
        <TextField
          disabled={!(isManager || isAdmin || isAccountant)}
          id="project_manager_name"
          required
          label="Project Manager Name"
          variant="outlined"
          fullWidth
          margin="normal"
          value={form.project_manager_name || ""}
          onChange={handleInputChange}
          inputProps={{
            maxLength: 255,
          }}
        />
        <TextField
          disabled={!(isManager || isAdmin || isAccountant)}
          id="project_manager_email"
          required
          label="Project Manager Email"
          variant="outlined"
          fullWidth
          margin="normal"
          value={form.project_manager_email || ""}
          onChange={handleInputChange}
          inputProps={{
            maxLength: 255,
          }}
        />
        <Autocomplete
          //disabled={!(isManager || isAdmin || isAccountant)}
          options={units}
          getOptionLabel={(option) => option.unit_name || ""}
          value={units.find((unit) => unit.unit_id === form.unit_id) || null}
          required
          onChange={(event, newValue) =>
            handleInputChange({
              target: {
                id: "unit_id",
                value: newValue ? newValue.unit_id : null,
              },
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Unit"
              required
              variant="outlined"
              margin="normal"
              helperText="The main EBS unit in charge of this project​"
              fullWidth
            />
          )}
        />

        <TextField
          id="name"
          required
          label="Project Name"
          variant="outlined"
          fullWidth
          margin="normal"
          value={form.name || ""}
          onChange={handleInputChange}
          InputLabelProps={{ shrink: form.name !== "" && form.name != null }}
          inputProps={{
            maxLength: 255,
          }}
        />

        <TextField
          id="acronym"
          label="Acronym"
          variant="outlined"
          fullWidth
          margin="normal"
          value={form.acronym || ""}
          onChange={handleInputChange}
          InputLabelProps={{
            shrink: form.acronym !== "" && form.acronym != null,
          }}
          inputProps={{
            maxLength: 10,
          }}
        />

        <Autocomplete
          options={activityOptions ?? []}
          getOptionLabel={(option) => option.activity_name}
          isOptionEqualToValue={(option, value) =>
            option.activity_id === value.activity_id
          }
          value={activity}
          onChange={(event, newValue) =>
            handleInputChange({
              target: {
                id: "activity_id",
                value: newValue ? newValue.activity_id : null,
              },
            })
          }
          renderOption={(props, option) => (
            <li {...props}>
              <Chip
                label={option.activity_code}
                size="small"
                sx={{ px: 0, mr: 1, width: 35, flex: "0 0 35px" }}
              />
              {option.activity_name}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Activity"
              required
              variant="outlined"
              margin="normal"
              helperText="The main type of activity carried on in this project​"
              fullWidth
              InputProps={{
                ...params.InputProps,
                startAdornment: activity ? (
                  <>
                    <Chip
                      size="small"
                      label={activity?.activity_code}
                      sx={{
                        px: 0,
                        mr: 1,
                        fontWeight: "bold",
                        width: 35,
                        flex: "0 0 35px",
                      }}
                    />
                    {params.InputProps.startAdornment}
                  </>
                ) : (
                  params.InputProps.startAdornment
                ),
              }}
            />
          )}
        />
        {categories &&
          categories.map((c, i) => {
            const options =
              c.parent_category_id === null
                ? c.subactivities
                : (c.subactivities?.filter(
                    (s) =>
                      s.parent_subactivity_id ===
                      form.subactivities?.[i - 1]?.subactivity_id,
                  ) ?? []);
            if (options.length === 0) return <Box key={i}></Box>;
            return (
              <Autocomplete
                key={i}
                options={options}
                isOptionEqualToValue={(option, value) =>
                  option.subactivity_id === value.subactivity_id
                }
                getOptionLabel={(option) => option.subactivity_name || ""}
                value={form.subactivities?.[i] || null}
                onChange={(event, newValue) => {
                  const newSubactivities = [...(form.subactivities ?? [])];
                  newSubactivities[i] = newValue ?? null;
                  if (
                    newSubactivities.length > i + 1 &&
                    newSubactivities[i + 1]?.parent_subactivity_id
                  ) {
                    for (
                      let index = i + 1;
                      index < newSubactivities.length;
                      index++
                    ) {
                      newSubactivities[index] = null;
                    }
                  }
                  handleInputChange({
                    target: {
                      id: "subactivities",
                      value: newSubactivities,
                    },
                  });
                }}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Chip
                      label={option.subactivity_code}
                      size="small"
                      sx={{
                        px: 0,
                        mr: 1,
                        fontWeight: "bold",
                        width: 20 + c.code_length * 15,
                        flex: `0 0 ${20 + c.code_length * 15}px`,
                      }}
                    />
                    {option.subactivity_name}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={c.category_name}
                    variant="outlined"
                    required={c.required}
                    margin="normal"
                    helperText="The optional specification of activity carried on in this project​"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      startAdornment:
                        form.subactivities?.[i] || null ? (
                          <>
                            <Chip
                              size="small"
                              label={`${
                                (form.subactivities?.[i] || null)
                                  ?.subactivity_code ?? ""
                              }`}
                              sx={{
                                px: 0,
                                mr: 1,
                                fontWeight: "bold",
                                width: 20 + c.code_length * 15,
                                flex: `0 0 ${20 + c.code_length * 15}px`,
                              }}
                            />
                            {params.InputProps.startAdornment}
                          </>
                        ) : (
                          params.InputProps.startAdornment
                        ),
                    }}
                  />
                )}
              />
            );
          })}
        <TextField
          id="description"
          sx={{ resize: "both" }}
          required
          label="Project Description"
          variant="outlined"
          fullWidth
          margin="normal"
          multiline
          rows={20}
          resize="true"
          value={form.description || ""}
          onChange={handleInputChange}
          error={tooLong}
          helperText={
            tooLong
              ? `Maximum ${maxLength} characters allowed — you have ${form.description.length}.`
              : "Overview of project goals, schedule and activities. Max 2500 characters.​"
          }
          InputLabelProps={{
            shrink: form.description !== "" && form.description != null,
          }}
          ref={descriptionRef}
        />
        <Box className="dateFieldContainer">
          <LocalizationProvider
            dateAdapter={AdapterMoment}
            adapterLocale="en-gb"
          >
            <DatePicker
              label="Start Date"
              slotProps={{
                textField: {
                  helperText: "The start of project activities",
                  required: true,
                },
              }}
              id="start_date"
              value={
                form.start_date ? moment(form.start_date, "YYYY-MM-DD") : null
              } // Ensure valid moment
              maxDate={moment(form.end_date, "YYYY-MM-DD")}
              onChange={(newValue) =>
                handleInputChange({
                  target: {
                    id: "start_date",
                    value: newValue ? newValue.format("YYYY-MM-DD") : null,
                  },
                })
              }
              format="DD.MM.YYYY" // Display in DD.MM.YYYY format
            />
            <DatePicker
              label="End Date"
              slotProps={{
                textField: {
                  helperText: "The end of project activities",
                  required: true,
                },
              }}
              id="end_date"
              value={form.end_date ? moment(form.end_date, "YYYY-MM-DD") : null}
              minDate={moment(form.start_date, "YYYY-MM-DD")}
              onChange={(newValue) =>
                handleInputChange({
                  target: {
                    id: "end_date",
                    value: newValue ? newValue.format("YYYY-MM-DD") : null,
                  },
                })
              }
              format="DD.MM.YYYY"
            />
          </LocalizationProvider>
        </Box>
        <TextField
          id="client_name"
          required
          label="Client Name"
          helperText="Contract partner or paying party​"
          variant="outlined"
          fullWidth
          margin="normal"
          value={form.client_name || ""}
          onChange={handleInputChange}
          InputLabelProps={{
            shrink: form.client_name !== "" && form.client_name != null,
          }}
          inputProps={{
            maxLength: 255,
          }}
        />
        <Autocomplete
          options={allExpensesConfirmers}
          getOptionLabel={(option) => `${option.name} (${option.email})`}
          isOptionEqualToValue={(option, value) =>
            option.email === value.email && option.name === value.name
          }
          value={
            allExpensesConfirmers.find(
              (emp) =>
                emp.email === form.expenses_confirmed_by_email &&
                emp.name === form.expenses_confirmed_by_name,
            ) || null
          }
          onChange={(event, newValue) => {
            handleInputChange({
              target: {
                id: "expenses_confirmed",
                value: newValue ? newValue : null,
              },
            });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="Expenses Confirmed By"
              variant="outlined"
              fullWidth
              margin="normal"
            />
          )}
        />

        <DynamicBudgetInput
          unit_id={form.unit_id}
          budget={budget}
          setBudget={setBudget}
          setEbsTotal={setEbsTotal}
          internalStaffCost={internalStaffCost}
        />

        {budgetError && (
          <Alert ref={errorRef} severity="error" sx={{ mt: 2 }}>
            {budgetError}
          </Alert>
        )}

        <DynamicRevenueSharing
          rows={revenueSharing}
          setRows={setRevenueSharing}
          units={units}
          unitId={unitId}
          isAccountant={isAccountant}
          isManager={isManager}
          isAdmin={isAdmin}
          total={ebsTotal}
        />

        <ProjectMembersManager
          employees={employees}
          members={members}
          setMembers={setMembers}
          internalStaffCost={internalStaffCost}
          setInternalStaffCost={setInternalStaffCost}
          canEdit={true}
          isAdmin={isAdmin}
          projectManagerEmail={form.project_manager_email}
        />

        <TextField
          id="notes"
          label="Notes"
          helperText="Additional information about the project for review and accounting"
          variant="outlined"
          fullWidth
          margin="normal"
          multiline
          minRows={2}
          value={form.notes || ""}
          onChange={handleInputChange}
          InputLabelProps={{
            shrink: form.notes !== "" && form.notes != null,
          }}
        />
        <Box
          direction="row"
          spacing={2}
          sx={{
            position: "relative",
            border: "1px solid rgba(0, 0, 0, 0.23)", // matches TextField default
            borderRadius: 1,
            padding: 2,
          }}
        >
          <FormControlLabel
            label="Requires Monthly Progress Reporting"
            labelPlacement="end"
            control={
              <Switch
                checked={form?.requires_progress_reporting ?? false}
                onChange={handleInputChange}
                id="requires_progress_reporting"
              />
            }
            sx={{
              // make the label span only hug its text
              "& .MuiFormControlLabel-label": {
                cursor: "pointer",
                width: "auto",
              },
              width: "fit-content", // collapse whole thing
            }}
          />
        </Box>
        <Box
          direction="row"
          spacing={2}
          sx={{
            position: "relative",
            border: "1px solid rgba(0, 0, 0, 0.23)", // matches TextField default
            borderRadius: 1,
            padding: 2,
          }}
        >
          <FormControlLabel
            label="Sync To TimeEdit"
            labelPlacement="end"
            control={
              <Switch
                checked={form?.sync_to_timeedit ?? false}
                onChange={handleInputChange}
                id="sync_to_timeedit"
              />
            }
            sx={{
              // make the label span only hug its text
              "& .MuiFormControlLabel-label": {
                cursor: "pointer",
                width: "auto",
              },
              width: "fit-content", // collapse whole thing
            }}
          />
        </Box>
        {form?.sync_to_timeedit && (
          <Autocomplete
            options={timeEditTermCategories}
            value={form.timeedit_term}
            onChange={(event, newValue) => {
              handleInputChange({
                target: {
                  id: "timeedit_term",
                  value: newValue ? newValue : null,
                },
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="TimeEdit Term"
                variant="outlined"
                fullWidth
                margin="normal"
              />
            )}
          />
        )}

        <input
          type="hidden"
          name="isDraft"
          value={isDraft ? "true" : "false"}
        />
        <Attachments
          allowedTypes={allowedTypes}
          allowedExtensions={allowedExtensions}
          uploads={uploads}
          setUploads={setUploads}
          uid={form.uid}
        ></Attachments>

        {isAdmin && (
          <>
            <Box
              sx={{
                position: "relative",
                border: "1px solid rgba(0, 0, 0, 0.23)", // matches TextField default
                borderRadius: 1,
                padding: 2,
              }}
            >
              <FormControlLabel
                label={`Sync to My Hours on Save/Apply${form.uid && !form.code ? ` with temporary code: ${form.uid}` : ""}`}
                labelPlacement="end"
                control={
                  <Switch
                    checked={form?.syncToMyHours ?? false}
                    onChange={handleInputChange}
                    id="syncToMyHours"
                  />
                }
                sx={{
                  // make the label span only hug its text
                  "& .MuiFormControlLabel-label": {
                    cursor: "pointer",
                    width: "auto",
                  },
                  width: "fit-content", // collapse whole thing
                }}
              />
            </Box>
            <Autocomplete
              options={statusOptions}
              getOptionLabel={(option) => option.status_name}
              isOptionEqualToValue={(option, value) =>
                option.status_id === value.status_id
              }
              value={
                statusOptions.find(
                  (option) => option.status_id === form.status_id,
                ) || null
              }
              onChange={(event, newValue) => {
                handleInputChange({
                  target: {
                    id: "status_id",
                    value: newValue ? newValue.status_id : null,
                  },
                });
              }}
              renderOption={(props, option) => (
                <li {...props}>
                  <Chip
                    label={option.status_id}
                    size="small"
                    sx={{ px: 0, mr: 1, width: 40, flex: "0 0 40px" }}
                  />
                  {option.status_name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Status"
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: form.status_id ? (
                      <>
                        <Chip
                          size="small"
                          label={form.status_id}
                          sx={{
                            px: 0,
                            mr: 1,
                            fontWeight: "bold",
                            width: 40,
                            flex: "0 0 40px",
                          }}
                        />
                        {params.InputProps.startAdornment}
                      </>
                    ) : (
                      params.InputProps.startAdornment
                    ),
                  }}
                />
              )}
            />
          </>
        )}
        <Box id="buttonContainer" className="buttonContainer" sx={{ pt: 3 }}>
          {(!form.status_id ||
            form.status_id === statuses.DRAFT ||
            form.status_id === statuses.PREPARING_FOR_SIGNOFF) && (
            <Button
              variant="formSubmit"
              color="primary"
              type="submit"
              onClick={() => {
                setIsDraft(false);
                setSave(false);
              }}
            >
              Apply
            </Button>
          )}
          {(!form.status_id || form.status_id === statuses.DRAFT) && (
            <Button
              variant="formSubmit"
              color="primary"
              type="submit"
              onClick={() => setIsDraft(true)}
              formNoValidate
            >
              Save Draft
            </Button>
          )}
          {(isAdmin ||
            isAccountant ||
            isManager ||
            form.status_id === statuses.PREPARING_FOR_SIGNOFF) && (
            <Button
              variant="formSubmit"
              color="primary"
              type="submit"
              onClick={() => {
                setIsDraft(false);
                setSave(true);
              }}
            >
              Save
            </Button>
          )}
        </Box>
        {resultMessage && (
          <Typography
            variant="body1"
            color={resultMessage.includes("failed") ? "error" : "success"}
          >
            {resultMessage}
          </Typography>
        )}
      </form>

      {/* Apply Confirmation Dialogue */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogTitle>Submit Project for Confirmation?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will lock your project for editing and send it to be confirmed
            by the unit manager and accounting! Please confirm that your project
            is ready to be submitted for review.
          </DialogContentText>
          {form.syncToMyHours && (
            <MyHoursCompare
              newCode={form.code}
              project={form}
              syncToMyHours={form.syncToMyHours}
              setSyncToMyHours={(checked) => {
                handleInputChange({
                  target: {
                    id: "syncToMyHours",
                    value: "",
                    type: "checkbox",
                    checked: checked,
                  },
                });
              }}
              setAlert={setAlert}
            ></MyHoursCompare>
          )}
        </DialogContent>
        <DialogActions sx={{ gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setConfirmDialogOpen(false)}
            //color="secondary"
          >
            No, I want to refine my project
          </Button>
          <Button
            variant="formSubmit"
            onClick={async () => {
              setLoading(true);
              try {
                if (pendingFormData) {
                  await handleSubmit(undefined);
                }
              } finally {
                setLoading(false);
                setConfirmDialogOpen(false);
              }
            }}
            color="primary"
            autoFocus
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? "Submitting..." : "Yes"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={mhConfirmDialogOpen}
        onClose={() => setMhConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm sync to My Hours</DialogTitle>
        <DialogContent>
          <MyHoursCompare
            newCode={form.code}
            project={form}
            syncToMyHours={form.syncToMyHours}
            setSyncToMyHours={(checked) => {
              handleInputChange({
                target: {
                  id: "syncToMyHours",
                  value: "",
                  type: "checkbox",
                  checked: checked,
                },
              });
            }}
            setAlert={setAlert}
          ></MyHoursCompare>
        </DialogContent>
        <DialogActions sx={{ gap: 1.5 }}>
          <Button
            onClick={() => setMhConfirmDialogOpen(false)}
            color="secondary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleSave();
            }}
            variant="contained"
            color="primary"
            disabled={saving}
          >
            {saving
              ? "Saving…"
              : form.syncToMyHours
                ? "Save and sync"
                : "Save without sync"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EditProjectForm;
